import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchManufacturers,
  createManufacturerThunk,
  updateManufacturerThunk,
  deleteManufacturerThunk,
  fetchManufacturerProductsThunk,
} from "../../../../redux/Thunk/Product/ProductThunks";
import Pagination from "../../../common/Pagination";

const SORT_OPTIONS = [
  {
    id: "nameAsc",
    label: "Name (A → Z)",
    description: "Alphabetical, ascending",
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
      </svg>
    ),
  },
  {
    id: "nameDesc",
    label: "Name (Z → A)",
    description: "Alphabetical, descending",
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h6m-6 4h9m-9 4h13m-4 4l4 4m0 0l4-4m-4 4V8" />
      </svg>
    ),
  },
  {
    id: "countDesc",
    label: "Most Products First",
    description: "Highest assigned products",
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
  },
  {
    id: "countAsc",
    label: "Least Products First",
    description: "Lowest assigned products",
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
      </svg>
    ),
  },
  {
    id: "newest",
    label: "Recently Added",
    description: "Latest created first",
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
];

const Manufacturers = () => {
  const dispatch = useDispatch();
  const manufacturersFromState = useSelector((state) => state?.product?.manufacturersList);
  const rawManufacturers = useMemo(() => manufacturersFromState || [], [manufacturersFromState]);

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // 'all', 'withProducts', 'zeroProducts'
  const [sortBy, setSortBy] = useState("nameAsc");
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortDropdownRef = useRef(null);
  const [page, setPage] = useState(1);
  const limit = 15;

  // Modals state
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // null = add, object = edit
  const [modalName, setModalName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerManufacturer, setDrawerManufacturer] = useState(null);
  const [drawerProducts, setDrawerProducts] = useState([]);
  const [loadingDrawer, setLoadingDrawer] = useState(false);

  // Load manufacturers with product count
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      await dispatch(fetchManufacturers({ withCount: true })).unwrap();
    } catch (err) {
      console.error("Failed to load manufacturers:", err);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Click outside listener for sort dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target)) {
        setIsSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentSortOption = useMemo(
    () => SORT_OPTIONS.find((opt) => opt.id === sortBy) || SORT_OPTIONS[0],
    [sortBy]
  );

  // Statistics
  const stats = useMemo(() => {
    const total = rawManufacturers.length;
    let withProducts = 0;
    let zeroProducts = 0;
    let totalAssignedProducts = 0;

    rawManufacturers.forEach((m) => {
      const count = parseInt(m.productsCount || 0, 10);
      if (count > 0) {
        withProducts += 1;
        totalAssignedProducts += count;
      } else {
        zeroProducts += 1;
      }
    });

    return { total, withProducts, zeroProducts, totalAssignedProducts };
  }, [rawManufacturers]);

  // Filter & Sort
  const processedManufacturers = useMemo(() => {
    let result = [...rawManufacturers];

    // Search filter
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      result = result.filter((m) => (m.name || "").toLowerCase().includes(q));
    }

    // Category filter
    if (filterType === "withProducts") {
      result = result.filter((m) => parseInt(m.productsCount || 0, 10) > 0);
    } else if (filterType === "zeroProducts") {
      result = result.filter((m) => parseInt(m.productsCount || 0, 10) === 0);
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === "nameAsc") {
        return (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
      }
      if (sortBy === "nameDesc") {
        return (b.name || "").localeCompare(a.name || "", undefined, { sensitivity: "base" });
      }
      if (sortBy === "countDesc") {
        return parseInt(b.productsCount || 0, 10) - parseInt(a.productsCount || 0, 10);
      }
      if (sortBy === "countAsc") {
        return parseInt(a.productsCount || 0, 10) - parseInt(b.productsCount || 0, 10);
      }
      if (sortBy === "newest") {
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
      return 0;
    });

    return result;
  }, [rawManufacturers, searchTerm, filterType, sortBy]);

  // Pagination slice
  const totalPages = Math.ceil(processedManufacturers.length / limit) || 1;
  const paginatedList = useMemo(() => {
    const start = (page - 1) * limit;
    return processedManufacturers.slice(start, start + limit);
  }, [processedManufacturers, page, limit]);

  // Open Add Modal
  const handleOpenAdd = () => {
    setEditingItem(null);
    setModalName("");
    setIsAddEditModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setModalName(item.name || "");
    setIsAddEditModalOpen(true);
  };

  // Submit Add / Edit
  const handleSubmitAddEdit = async (e) => {
    e.preventDefault();
    const name = modalName.trim();
    if (!name || isSubmitting) return;

    setIsSubmitting(true);
    try {
      if (editingItem) {
        await dispatch(
          updateManufacturerThunk({ id: editingItem.id, name, oldName: editingItem.name })
        ).unwrap();
      } else {
        await dispatch(createManufacturerThunk({ name })).unwrap();
      }
      setIsAddEditModalOpen(false);
      loadData();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open Delete Modal
  const handleOpenDelete = (item) => {
    setDeletingItem(item);
    setIsDeleteModalOpen(true);
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!deletingItem || isDeleting) return;
    setIsDeleting(true);
    try {
      await dispatch(deleteManufacturerThunk({ id: deletingItem.id })).unwrap();
      setIsDeleteModalOpen(false);
      setDeletingItem(null);
      loadData();
    } catch (err) {
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Open Products Drawer
  const handleOpenDrawer = async (item) => {
    setDrawerManufacturer(item);
    setIsDrawerOpen(true);
    setLoadingDrawer(true);
    try {
      const data = await dispatch(fetchManufacturerProductsThunk({ id: item.id })).unwrap();
      setDrawerProducts(data?.products || []);
    } catch (err) {
      console.error(err);
      setDrawerProducts([]);
    } finally {
      setLoadingDrawer(false);
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    if (!rawManufacturers || rawManufacturers.length === 0) return;
    const headers = ["ID", "Manufacturer Name", "Assigned Products Count", "Created At"];
    const rows = rawManufacturers.map((m) => [
      m.id,
      `"${(m.name || "").replace(/"/g, '""')}"`,
      m.productsCount || 0,
      m.createdAt ? new Date(m.createdAt).toLocaleDateString() : "",
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `manufacturers_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="w-full space-y-6">
      {/* Top Statistic Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Total Manufacturers */}
        <div
          onClick={() => {
            setFilterType("all");
            setPage(1);
          }}
          className={`p-4 rounded-2xl border shadow-xs flex items-center justify-between transition-all cursor-pointer ${
            filterType === "all"
              ? "bg-white border-primary-300 ring-2 ring-primary-500/10"
              : "bg-white border-gray-200/80 hover:border-primary-200"
          }`}
        >
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
              Total Manufacturers
            </span>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-black text-gray-900 tracking-tight">
                {stats.total}
              </span>
              <span className="text-xs font-semibold text-gray-500">Companies</span>
            </div>
            <p className="text-[11px] text-gray-400 mt-1">Master catalog</p>
          </div>
          <div className="w-11 h-11 rounded-2xl bg-primary-50 text-primary-600 flex items-center justify-center shrink-0 border border-primary-100">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
        </div>

        {/* Assigned to Products */}
        <div
          onClick={() => {
            setFilterType("withProducts");
            setPage(1);
          }}
          className={`p-4 rounded-2xl border shadow-xs flex items-center justify-between transition-all cursor-pointer ${
            filterType === "withProducts"
              ? "bg-emerald-50/40 border-emerald-300 ring-2 ring-emerald-500/10"
              : "bg-white border-gray-200/80 hover:border-emerald-200"
          }`}
        >
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
              Active in Inventory
            </span>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-black text-emerald-600 tracking-tight">
                {stats.withProducts}
              </span>
              <span className="text-xs font-semibold text-emerald-700/80">With Products</span>
            </div>
            <p className="text-[11px] text-gray-400 mt-1">
              {stats.totalAssignedProducts} total product assignments
            </p>
          </div>
          <div className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 border border-emerald-100">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>

        {/* Zero Products */}
        <div
          onClick={() => {
            setFilterType("zeroProducts");
            setPage(1);
          }}
          className={`p-4 rounded-2xl border shadow-xs flex items-center justify-between transition-all cursor-pointer ${
            filterType === "zeroProducts"
              ? "bg-amber-50/40 border-amber-300 ring-2 ring-amber-500/10"
              : "bg-white border-gray-200/80 hover:border-amber-200"
          }`}
        >
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
              Zero Products
            </span>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-black text-amber-600 tracking-tight">
                {stats.zeroProducts}
              </span>
              <span className="text-xs font-semibold text-amber-700/80">Unassigned</span>
            </div>
            <p className="text-[11px] text-gray-400 mt-1">Safe to delete or link</p>
          </div>
          <div className="w-11 h-11 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0 border border-amber-100">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs overflow-hidden">
        {/* Controls Bar */}
        <div className="p-4 sm:p-5 border-b border-gray-100 space-y-3.5">
          {/* Top Tier: Search & Action Buttons */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {/* Search Input */}
            <div className="relative w-full sm:w-80 md:w-96">
              <svg
                className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                placeholder="Search manufacturers by name..."
                className="w-full pl-9 pr-8 py-2 text-xs bg-gray-50/80 border border-gray-200 rounded-xl outline-none focus:border-primary-500 focus:bg-white focus:ring-2 focus:ring-primary-500/10 transition-all font-medium text-gray-800 shadow-2xs"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm("");
                    setPage(1);
                  }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  title="Clear search"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2.5 shrink-0 self-end sm:self-auto">
              {/* Export CSV */}
              <button
                type="button"
                onClick={handleExportCSV}
                className="px-3.5 py-2 text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl transition-all shadow-2xs flex items-center gap-1.5 cursor-pointer"
                title="Export current manufacturer list to CSV"
              >
                <svg className="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Export</span>
              </button>

              {/* Add Manufacturer Button */}
              <button
                type="button"
                onClick={handleOpenAdd}
                className="px-4 py-2 text-xs font-bold text-white bg-primary-600 hover:bg-primary-700 rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                <span>New Manufacturer</span>
              </button>
            </div>
          </div>

          {/* Bottom Tier: Filter Tabs & Custom Sort Dropdown */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2.5 border-t border-gray-100">
            {/* Filter Tabs / Pills */}
            <div className="inline-flex p-1 bg-gray-100/90 rounded-xl text-xs font-semibold shrink-0 w-fit">
              <button
                type="button"
                onClick={() => {
                  setFilterType("all");
                  setPage(1);
                }}
                className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                  filterType === "all"
                    ? "bg-white text-gray-900 shadow-2xs font-bold"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                All ({stats.total})
              </button>
              <button
                type="button"
                onClick={() => {
                  setFilterType("withProducts");
                  setPage(1);
                }}
                className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                  filterType === "withProducts"
                    ? "bg-white text-emerald-700 shadow-2xs font-bold"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                Active ({stats.withProducts})
              </button>
              <button
                type="button"
                onClick={() => {
                  setFilterType("zeroProducts");
                  setPage(1);
                }}
                className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                  filterType === "zeroProducts"
                    ? "bg-white text-amber-700 shadow-2xs font-bold"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                Zero ({stats.zeroProducts})
              </button>
            </div>

            {/* Right: Results Count & Custom Sort Dropdown */}
            <div className="flex items-center gap-3 shrink-0 self-end sm:self-auto">
              <span className="text-[11px] text-gray-400 font-medium hidden md:inline">
                Showing {processedManufacturers.length > 0 ? (page - 1) * limit + 1 : 0}–{Math.min(page * limit, processedManufacturers.length)} of {processedManufacturers.length}
              </span>

              {/* Custom Sort Dropdown */}
              <div className="relative" ref={sortDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsSortOpen((prev) => !prev)}
                  className={`px-3 py-2 text-xs font-semibold rounded-xl border transition-all flex items-center gap-2 cursor-pointer shadow-2xs ${
                    isSortOpen
                      ? "bg-primary-50/70 border-primary-300 text-primary-900 ring-2 ring-primary-500/10"
                      : "bg-white hover:bg-gray-50 border-gray-200 text-gray-700"
                  }`}
                  aria-expanded={isSortOpen}
                  aria-haspopup="listbox"
                >
                  <div className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                    </svg>
                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                      Sort:
                    </span>
                    <span className="text-gray-900 font-bold">
                      {currentSortOption?.label || "Name (A → Z)"}
                    </span>
                  </div>
                  <svg
                    className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform duration-200 ${
                      isSortOpen ? "rotate-180 text-primary-600" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu Popover */}
                {isSortOpen && (
                  <div className="absolute right-0 top-full mt-1.5 w-60 bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-1.5 animate-in fade-in zoom-in-95 duration-100">
                    <div className="px-2.5 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">
                      Sort Manufacturers
                    </div>
                    <div className="space-y-0.5">
                      {SORT_OPTIONS.map((opt) => {
                        const isSelected = sortBy === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => {
                              setSortBy(opt.id);
                              setPage(1);
                              setIsSortOpen(false);
                            }}
                            className={`w-full text-left px-2.5 py-2 text-xs rounded-lg flex items-center justify-between transition-colors cursor-pointer ${
                              isSelected
                                ? "bg-primary-50 text-primary-700 font-bold"
                                : "text-gray-700 hover:bg-gray-50 hover:text-gray-900 font-medium"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className={isSelected ? "text-primary-600" : "text-gray-400"}>
                                {opt.icon}
                              </span>
                              <div>
                                <div className="leading-tight">{opt.label}</div>
                                <div className="text-[10px] text-gray-400 font-normal">
                                  {opt.description}
                                </div>
                              </div>
                            </div>
                            {isSelected && (
                              <svg className="w-4 h-4 text-primary-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/70 border-b border-gray-100 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                <th className="py-3 px-4 w-16">#ID</th>
                <th className="py-3 px-4">Manufacturer Name</th>
                <th className="py-3 px-4 w-44">Assigned Products</th>
                <th className="py-3 px-4 w-36">Added On</th>
                <th className="py-3 px-4 w-28 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-xs">
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-gray-400">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                      <span className="font-semibold text-xs">Loading manufacturers...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedList.length > 0 ? (
                paginatedList.map((item) => {
                  const count = parseInt(item.productsCount || 0, 10);
                  return (
                    <tr key={item.id} className="hover:bg-gray-50/80 transition-colors group">
                      <td className="py-3.5 px-4 font-mono font-medium text-gray-400 text-[11px]">
                        #{item.id}
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                          {item.name}
                        </span>
                      </td>
                      <td className="py-3.5 px-4">
                        <button
                          type="button"
                          onClick={() => handleOpenDrawer(item)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                            count > 0
                              ? "bg-emerald-50 text-emerald-800 border border-emerald-200/60 hover:bg-emerald-100"
                              : "bg-gray-100 text-gray-500 border border-gray-200/60 hover:bg-gray-200"
                          }`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                          <span>{count} product{count === 1 ? "" : "s"}</span>
                          <svg className="w-3 h-3 text-current ml-0.5 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </td>
                      <td className="py-3.5 px-4 text-gray-500 font-medium">
                        {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Edit Button */}
                          <button
                            type="button"
                            onClick={() => handleOpenEdit(item)}
                            title="Edit / Rename manufacturer"
                            className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>

                          {/* Delete Button */}
                          <button
                            type="button"
                            onClick={() => handleOpenDelete(item)}
                            title={count > 0 ? "Cannot delete: products assigned" : "Delete manufacturer"}
                            className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                              count > 0
                                ? "text-gray-300 hover:text-amber-600 hover:bg-amber-50"
                                : "text-gray-400 hover:text-rose-600 hover:bg-rose-50"
                            }`}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-gray-400">
                    <p className="font-semibold text-sm">No manufacturers found</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {searchTerm ? "Try searching with a different term" : "Click 'New Manufacturer' to add one"}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {processedManufacturers.length > limit && (
          <div className="p-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500">
              Showing {(page - 1) * limit + 1} to{" "}
              {Math.min(page * limit, processedManufacturers.length)} of{" "}
              {processedManufacturers.length} manufacturers
            </span>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={(newPage) => setPage(newPage)}
            />
          </div>
        )}
      </div>

      {/* Add / Edit Manufacturer Modal */}
      {isAddEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150">
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  {editingItem ? "Edit Manufacturer" : "Add New Manufacturer"}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {editingItem
                    ? "Renaming updates all associated products in inventory automatically."
                    : "Add a new company to the master manufacturer catalog."}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsAddEditModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmitAddEdit}>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                    Manufacturer Name*
                  </label>
                  <input
                    type="text"
                    required
                    autoFocus
                    value={modalName}
                    onChange={(e) => setModalName(e.target.value)}
                    placeholder="e.g. Sun Pharmaceutical Industries Ltd"
                    className="w-full px-3.5 py-2.5 text-xs font-medium text-gray-900 bg-white border border-gray-200 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10 transition-all shadow-2xs"
                  />
                </div>

                {editingItem && parseInt(editingItem.productsCount || 0, 10) > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200/80 rounded-xl flex items-start gap-2.5 text-xs text-amber-800">
                    <svg className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>
                      This manufacturer is assigned to{" "}
                      <strong>{editingItem.productsCount} products</strong>. Renaming will cascade and update the manufacturer on all these products automatically!
                    </span>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setIsAddEditModalOpen(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!modalName.trim() || isSubmitting}
                  className="px-5 py-2 text-xs font-bold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 rounded-xl transition-all shadow-xs cursor-pointer"
                >
                  {isSubmitting ? "Saving..." : editingItem ? "Update Manufacturer" : "Create Manufacturer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && deletingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150">
            <div className="p-6">
              <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-4 border border-rose-100">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>

              <h3 className="text-base font-bold text-gray-900">
                Delete "{deletingItem.name}"?
              </h3>

              {parseInt(deletingItem.productsCount || 0, 10) > 0 ? (
                <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-800">
                  <p className="font-bold mb-1">Cannot Delete</p>
                  <p>
                    This manufacturer is currently linked to{" "}
                    <strong>{deletingItem.productsCount} active products</strong>. To delete it, please reassign those products to another manufacturer first.
                  </p>
                </div>
              ) : (
                <p className="text-xs text-gray-500 mt-2">
                  Are you sure you want to permanently delete this manufacturer from the master catalog? This action cannot be undone.
                </p>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setDeletingItem(null);
                }}
                className="px-4 py-2 text-xs font-semibold text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              {parseInt(deletingItem.productsCount || 0, 10) === 0 && (
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className="px-5 py-2 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50 rounded-xl transition-all shadow-xs cursor-pointer"
                >
                  {isDeleting ? "Deleting..." : "Delete Manufacturer"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Slide-in Assigned Products Drawer */}
      {isDrawerOpen && drawerManufacturer && (
        <div className="fixed inset-0 z-50 flex justify-end bg-gray-900/40 backdrop-blur-xs">
          <div className="bg-white w-full max-w-lg h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
            {/* Drawer Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gray-50/80">
              <div>
                <span className="text-[10px] font-bold text-primary-600 uppercase tracking-wider">
                  Manufacturer Products
                </span>
                <h3 className="text-base font-bold text-gray-900 truncate max-w-sm">
                  {drawerManufacturer.name}
                </h3>
                <span className="text-xs text-gray-500">
                  {drawerProducts.length} assigned product{drawerProducts.length === 1 ? "" : "s"}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsDrawerOpen(false);
                  setDrawerManufacturer(null);
                  setDrawerProducts([]);
                }}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Drawer Body */}
            <div className="flex-1 overflow-y-auto p-6 divide-y divide-gray-100">
              {loadingDrawer ? (
                <div className="py-16 text-center text-gray-400 flex flex-col items-center justify-center gap-2">
                  <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-xs font-semibold">Loading products...</span>
                </div>
              ) : drawerProducts.length > 0 ? (
                drawerProducts.map((prod) => (
                  <div key={prod.id || prod.IID} className="py-3 first:pt-0 last:pb-0 flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-primary-50 text-primary-700 border border-primary-100">
                          #{prod.IID}
                        </span>
                        <span className="text-[10px] font-semibold text-gray-500 px-1.5 py-0.5 rounded bg-gray-100">
                          {prod.type || "Other"}
                        </span>
                      </div>
                      <h4 className="text-xs font-bold text-gray-900 truncate" title={prod.productName}>
                        {prod.productName}
                      </h4>
                      {prod.drugs && prod.drugs.length > 0 && (
                        <p className="text-[11px] text-gray-400 truncate mt-0.5">
                          {Array.isArray(prod.drugs) ? prod.drugs.join(", ") : prod.drugs}
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-xs font-bold text-gray-900 block">
                        ₹{prod.pricing?.mrp || 0}
                      </span>
                      <span className="text-[10px] font-medium text-gray-500">
                        Stock: {prod.productStock ?? 0}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-16 text-center text-gray-400">
                  <p className="text-xs font-semibold">No products currently assigned</p>
                  <p className="text-[11px] text-gray-400 mt-1">
                    Products with this manufacturer name will appear here automatically.
                  </p>
                </div>
              )}
            </div>

            {/* Drawer Footer */}
            <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end">
              <button
                type="button"
                onClick={() => {
                  setIsDrawerOpen(false);
                  setDrawerManufacturer(null);
                  setDrawerProducts([]);
                }}
                className="px-4 py-2 text-xs font-bold text-gray-700 bg-white border border-gray-200 rounded-xl shadow-2xs hover:bg-gray-50 cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Manufacturers;
